import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";
import { useAppStore } from "../../state/useAppStore";

type SpotKind = "risk" | "opportunity" | "client_request" | "operational" | "other";
type SpotState = "assessing" | "awaiting_approval" | "actioned" | "dismissed" | "expired";

interface Spot {
  id: string;
  workspaceId: string;
  submittedByUserId: string;
  kind: SpotKind;
  title: string;
  description: string;
  state: SpotState;
  currentDisposition: string | null;
  createdAt: string;
  impactEstimate: string | null;
  assessment?: { coas?: Array<{ coa: string; confidence: number; rationale: string }> } | null;
}

type QueueTab = "open" | "recent" | "signals";

const IMPACT_WEIGHT: Record<string, number> = {
  critical: 1.0,
  high: 0.8,
  medium: 0.5,
  low: 0.25,
};

function scoreSpot(s: Spot): number {
  // Phase B ranking heuristic: impact_estimate × top-COA confidence × recency.
  // Higher = more urgent. Pure function so test parity is trivial.
  const impact = IMPACT_WEIGHT[s.impactEstimate ?? "medium"] ?? 0.5;
  const conf = s.assessment?.coas?.[0]?.confidence ?? 0.5;
  const ageHrs = Math.max(
    0,
    (Date.now() - new Date(s.createdAt).getTime()) / 3_600_000,
  );
  // Decay: 1.0 at submission, ~0.5 at 48h, ~0.1 at 7d.
  const recency = Math.exp(-ageHrs / 72);
  return impact * conf * 0.6 + impact * recency * 0.4;
}

const KIND_LABELS: Record<SpotKind, string> = {
  risk: "Risk",
  opportunity: "Opportunity",
  client_request: "Client request",
  operational: "Operational",
  other: "Other",
};

const STATE_LABELS: Record<SpotState, string> = {
  assessing: "Assessing",
  awaiting_approval: "Awaiting approval",
  actioned: "Actioned",
  dismissed: "Dismissed",
  expired: "Expired",
};

export function Spotted() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [kind, setKind] = useState<SpotKind>("operational");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<"" | "low" | "medium" | "high" | "critical">("");
  const [clientTag, setClientTag] = useState("");
  const [attachments, setAttachments] = useState<
    Array<{ url: string; filename: string; mime: string; sizeBytes: number; previewUrl?: string }>
  >([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>("open");

  const listQ = useQuery<{ spots: Spot[]; total: number }, ApiError>({
    queryKey: ["spots", workspaceId],
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/spots?limit=100`),
    enabled: !!workspaceId,
  });

  const submitM = useMutation<{ spot: Spot }, ApiError, void>({
    mutationFn: () =>
      apiPost(`/api/workspaces/${workspaceId}/spots`, {
        kind,
        title: title.trim(),
        description: description.trim(),
        ...(impact && { impact_estimate: impact }),
        ...(clientTag.trim() && { client_tag: clientTag.trim() }),
        ...(attachments.length > 0 && {
          attachments: attachments.map((a) => ({
            url: a.url,
            filename: a.filename,
            mime: a.mime,
          })),
        }),
      }),
    onSuccess: async (data) => {
      setTitle("");
      setDescription("");
      setClientTag("");
      setImpact("");
      // Revoke any object URLs we created for previews before clearing.
      for (const a of attachments) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
      setAttachments([]);
      setFormOpen(false);
      await qc.invalidateQueries({ queryKey: ["spots", workspaceId] });
      navigate(`/workspaces/${workspaceId}/spotted/${data.spot.id}`);
    },
  });

  const canSubmit =
    title.trim().length >= 2 &&
    description.trim().length >= 2 &&
    !submitM.isPending &&
    !uploading;

  async function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const next: typeof attachments = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`/api/workspaces/${workspaceId}/uploads`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.message ?? `Upload failed (${res.status})`);
        }
        const json = (await res.json()) as {
          url: string; filename: string; mime: string; sizeBytes: number;
        };
        next.push({
          url: json.url,
          filename: json.filename,
          mime: json.mime,
          sizeBytes: json.sizeBytes,
          previewUrl: URL.createObjectURL(f),
        });
      }
      setAttachments((prev) => [...prev, ...next]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires onChange.
      e.target.value = "";
    }
  }

  function removeAttachment(i: number) {
    setAttachments((prev) => {
      const a = prev[i];
      if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
      return prev.filter((_, idx) => idx !== i);
    });
  }

  return (
    <div className="main-inner" data-testid="spotted">
      <div className="greet-row">
        <span className="greet">Spotted</span>
        <span className="greet-date">SIGNAL FROM THE GROUND</span>
      </div>
      <div className="subgreet">
        <div className="lede">
          See something — a risk, opportunity, or client signal — that doesn't fit your current tasks?
          Report it here. XAMI assesses and routes it to the right approver.
        </div>
      </div>

      <div style={{ marginTop: 16, marginBottom: 24 }}>
        {formOpen ? (
          <div className="spotted-form">
            <div className="spotted-form-row">
              <label className="spotted-field">
                <span>Kind</span>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as SpotKind)}
                  className="review-input"
                >
                  {(Object.keys(KIND_LABELS) as SpotKind[]).map((k) => (
                    <option key={k} value={k}>{KIND_LABELS[k]}</option>
                  ))}
                </select>
              </label>
              <label className="spotted-field">
                <span>Impact (optional)</span>
                <select
                  value={impact}
                  onChange={(e) => setImpact(e.target.value as typeof impact)}
                  className="review-input"
                >
                  <option value="">—</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </label>
            </div>

            <label className="spotted-field" style={{ marginTop: 12 }}>
              <span>Title</span>
              <input
                type="text"
                className="review-input"
                placeholder="One line — what did you notice?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label className="spotted-field" style={{ marginTop: 12 }}>
              <span>Description</span>
              <textarea
                rows={5}
                className="review-textarea"
                placeholder="What did you see, what makes it relevant, and any specifics XAMI should know about (client name, dollar impact, deadline)…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <div className="spotted-field" style={{ marginTop: 12 }}>
              <span>Photos (optional)</span>
              <div className="spotted-attach">
                {attachments.map((a, i) => (
                  <div key={i} className="spotted-attach-thumb">
                    {a.previewUrl ? (
                      <img src={a.previewUrl} alt={a.filename} />
                    ) : (
                      <div className="spotted-attach-filename">{a.filename}</div>
                    )}
                    <button
                      type="button"
                      className="spotted-attach-remove"
                      onClick={() => removeAttachment(i)}
                      aria-label={`Remove ${a.filename}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="spotted-attach-add">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={onPickFiles}
                    disabled={uploading}
                    style={{ display: "none" }}
                  />
                  <span>{uploading ? "Uploading…" : "📷 Add photo"}</span>
                </label>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-3, #64748b)", marginTop: 6 }}>
                On mobile, this opens the camera. Up to 8 MB per image.
              </div>
              {uploadError && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>
                  {uploadError}
                </div>
              )}
            </div>

            <label className="spotted-field" style={{ marginTop: 12 }}>
              <span>Client / account (optional)</span>
              <input
                type="text"
                className="review-input"
                placeholder="e.g. Northwind Logistics"
                value={clientTag}
                onChange={(e) => setClientTag(e.target.value)}
              />
            </label>

            {submitM.error && (
              <div className="review-banner error" style={{ marginTop: 12 }}>
                Could not submit ({submitM.error.status}).
              </div>
            )}

            <div className="review-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="review-btn approve"
                disabled={!canSubmit}
                onClick={() => submitM.mutate()}
              >
                {submitM.isPending ? "Assessing…" : "Submit spot"}
              </button>
              <button
                type="button"
                className="review-btn note"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="review-btn approve"
              onClick={() => {
                useAppStore
                  .getState()
                  .primeRailMessage(
                    "I want to report a spot. Walk me through it.",
                  );
              }}
            >
              💬 Tell XAMI
            </button>
            <button
              type="button"
              className="review-btn note"
              onClick={() => setFormOpen(true)}
            >
              + Use the form
            </button>
          </div>
        )}
      </div>

      <QueueSection
        spots={listQ.data?.spots ?? []}
        loading={listQ.isLoading}
        tab={tab}
        onTab={setTab}
        onOpen={(id) => navigate(`/workspaces/${workspaceId}/spotted/${id}`)}
      />
    </div>
  );
}

function QueueSection({
  spots,
  loading,
  tab,
  onTab,
  onOpen,
}: {
  spots: Spot[];
  loading: boolean;
  tab: QueueTab;
  onTab: (t: QueueTab) => void;
  onOpen: (id: string) => void;
}) {
  const partitioned = useMemo(() => {
    const open: Spot[] = [];
    const recent: Spot[] = [];
    const signals: Spot[] = [];
    for (const s of spots) {
      if (s.state === "assessing" || s.state === "awaiting_approval") open.push(s);
      else if (s.currentDisposition === "signal") signals.push(s);
      else recent.push(s);
    }
    // Open is ranked by urgency × confidence × recency — the approver's view.
    open.sort((a, b) => scoreSpot(b) - scoreSpot(a));
    return { open, recent, signals };
  }, [spots]);

  const visible =
    tab === "open" ? partitioned.open
    : tab === "signals" ? partitioned.signals
    : partitioned.recent;

  const tabs: Array<{ key: QueueTab; label: string; count: number }> = [
    { key: "open",    label: "Open",          count: partitioned.open.length },
    { key: "recent",  label: "Recent",        count: partitioned.recent.length },
    { key: "signals", label: "Signals filed", count: partitioned.signals.length },
  ];

  return (
    <>
      <div className="ho-tabs" role="tablist" style={{ marginBottom: 12 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`ho-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => onTab(t.key)}
          >
            {t.label}
            <span className="badge">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: "var(--text-3)" }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div className="ho-empty">
          {tab === "open" ? "Nothing awaiting approval."
            : tab === "signals" ? "No signals filed yet."
            : "No actioned spots yet."}
        </div>
      ) : (
        <div className="st-list">
          {visible.map((s) => {
            const top = s.assessment?.coas?.[0];
            const score = tab === "open" ? scoreSpot(s) : null;
            return (
              <div
                key={s.id}
                className="st-row"
                onClick={() => onOpen(s.id)}
              >
                <div className="st-l">
                  <span className="pill">{KIND_LABELS[s.kind]}</span>
                  {score != null && (
                    <span
                      className="db-score"
                      title={`urgency × confidence: ${score.toFixed(2)}`}
                    >
                      {(score * 100).toFixed(0)}
                    </span>
                  )}
                </div>
                <div className="st-b">
                  <div className="st-s">{s.title}</div>
                  <div className="st-p">
                    {STATE_LABELS[s.state]}
                    {s.currentDisposition ? ` · ${s.currentDisposition}` : ""}
                    {s.impactEstimate ? ` · ${s.impactEstimate} impact` : ""}
                    {top ? ` · top COA: ${top.coa} (${Math.round(top.confidence * 100)}%)` : ""}
                    {" · "}
                    {new Date(s.createdAt).toLocaleString(undefined, {
                      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className="st-r">
                  <button type="button" className="btn">Open</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
